import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import compact from 'lodash/compact';
import uniq from 'lodash/uniq';

import { UserStatus } from 'chaika-enums';

import type { Optional } from 'chaika-common-types';
import type { PracticeContactViewModel } from 'chaika-types';

import { OptionType } from '@components/Select';
import { SelectionButtonsValueParams, SelectionButtonsValueType } from '@components/SelectionButtons';
import { store } from '@store';
import { translate } from '@chaika/i18n/helper';
import { formatFullName } from '@utils/formatFullName';
import { userQueryOptions } from '@user/queries/userQueries';
import { userPracticeQueryOptions } from '@user/queries/userPracticeQueries';

import type { TaskFieldData } from '@common/Task';
import { useTaskForm } from '@common/Task/TaskRightMenu/context/TaskFormContext';
import { TaskAssigneeType } from '@common/Task/TaskRightMenu/constants';

import { isPracticeOfOrganization } from '../utils/isPracticeOfOrganization';

interface Data {
    isCreationMode: boolean;
    isEditMode: boolean;
    canEdit: boolean;
    isPracticeAssignee: boolean;
    assigneeTypeOptions: SelectionButtonsValueParams<TaskAssigneeType>[];
    assigneeType: TaskAssigneeType;
    defaultUsers: OptionType<string>[];
    authorData: TaskFieldData;
    executorData: TaskFieldData[];
    onAssigneeTypeSelect(value: SelectionButtonsValueType): void;
    onExecutorsSelect(options: OptionType<string>[]): void;
    setOnlyMe(): void;
}

export function useTaskParticipants(): Data {
    const { state, permissions, actions } = useTaskForm();
    const { authUser } = store;

    const userIds = uniq(
        compact([state.authUserId, state.authorId, state.requesterId, ...state.executorIds, state.actualExecutorId]),
    );
    const { data: users = [] } = useQuery({ ...userQueryOptions.list({ ids: userIds }), enabled: userIds.length > 0 });
    const { data: authUserPractices = [] } = useQuery(userPracticeQueryOptions.byUserId(authUser.id));

    const onAssigneeTypeSelect = (value: SelectionButtonsValueType): void => {
        const assigneeType = Object.values(TaskAssigneeType).find(type => type === value);

        if (assigneeType) {
            actions.setAssigneeType(assigneeType, findCurrentPractice(authUserPractices, authUser.organizationId)?.id);
        }
    };

    const onExecutorsSelect = (options: OptionType<string>[]): void => {
        actions.setExecutors(options.map(it => it.value));
    };

    const setOnlyMe = useCallback(() => {
        actions.setExecutors([authUser.id]);
    }, [authUser.id, actions]);

    return {
        isCreationMode: permissions.isCreationMode,
        isEditMode: state.isEditMode,
        canEdit: state.isEditMode && permissions.canEdit,
        isPracticeAssignee: state.assigneeType === TaskAssigneeType.Practice,
        assigneeTypeOptions: [
            { value: TaskAssigneeType.Executors, label: translate('executors') },
            { value: TaskAssigneeType.Practice, label: translate('practice') },
        ],
        assigneeType: state.assigneeType,
        defaultUsers: users.map(user => ({ label: formatFullName(user), value: user.id })),
        authorData: [permissions.author?.id, permissions.author && formatFullName(permissions.author)],
        executorData: permissions.executors.map(it => [
            it.id,
            `${formatFullName(it)}${it.status === UserStatus.Archived ? ` (${translate('archive')})` : ''}`,
        ]),
        onAssigneeTypeSelect,
        onExecutorsSelect,
        setOnlyMe,
    };
}

function findCurrentPractice(
    practices: PracticeContactViewModel[],
    organizationId: Optional<string>,
): PracticeContactViewModel | undefined {
    return practices.find(practice => isPracticeOfOrganization(practice, organizationId));
}
